<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import Swiper from 'swiper'
import { useRouter } from 'vue-router/composables'
import { v4 as uuid } from 'uuid'
import ModuleCard from './ModuleCard.vue'
import { useElementClick } from '@/composables/tracks'
import 'swiper/css'

interface Props {
  module: any
  config?: any
  cardClass?: string
  headerClass?: string
}

const props = defineProps<Props>()

const { elementClick } = useElementClick()
const router = useRouter()
const current = ref(0)
const swiper = ref<Swiper | null>(null)
function initSwiper() {
  swiper.value = new Swiper(`.banner.swiper.columnId-${props.module.id}`, {
    grabCursor: true,
    centeredSlides: true,
    slidesPerView: 'auto',
    slideToClickedSlide: false,
    loop: true,
    spaceBetween: 12,
    on: {
      init: (swiperInstance) => {
        current.value = swiperInstance.realIndex
      },
      slideChange: async (swiperInstance) => {
        current.value = swiperInstance.realIndex
      },
      click(swiperInstance: any, event) {
        const clickedSlide = (event.target as Element)?.closest('.swiper-slide')
        if (clickedSlide) {
          if (clickedSlide.classList.contains('swiper-slide-active')) {
            gotoBook()
          }
          else {
            const slides = Array.from(swiperInstance.slides)
            const clickedIndex = slides.indexOf(clickedSlide)
            if (clickedIndex !== -1)
              swiperInstance.slideTo(clickedIndex)
          }
        }
      },
    },
  })
}

const books = computed(() => {
  if (props.module?.resourceList?.records?.length > 10)
    return props.module.resourceList.records.slice(0, 10)

  return props.module?.resourceList?.records || []
})

const currentBook = computed(() => {
  return books.value[current.value]
})

function gotoCollection() {
  elementClick(`seeall_${props.module.id}`)
  router.push({ name: 'collection', params: { columnId: props.module.id } })
}

function gotoBook() {
  router.push({
    name: 'book',
    params: { contentId: currentBook.value.id },
  })
}

onMounted(() => {
  initSwiper()
})
</script>

<template>
  <ModuleCard
    :title-text="module.showName"
    :action-text-value="config?.texts?.seeAll || ''"
    :card-class="cardClass"
    :header-class="headerClass"
    @action="gotoCollection"
  >
    <div :class="`banner swiper columnId-${module.id} mt-4`">
      <div class="swiper-wrapper">
        <div v-for="(book, index) in books" :key="index" class="swiper-slide">
          <BizBookCover
            :id="uuid()"
            :src="book.contentCoverUrl || ''"
            :small-theater="!!book.small_theater"
            :extra="{
              module_name: module.showName,
              module_id: module.id,
              module_type: module.columnType,
              author: book.authorInfo.author,
              ...book,
            }"
            class="book-cover"
          />
        </div>
      </div>
    </div>

    <div class="mt-4 text-left" @click="gotoBook">
      <div class="font-600 truncate text-n-text-1 text-16px">
        {{ currentBook.contentName }}
      </div>

      <div v-if="currentBook.tagList && currentBook.tagList.length" class="overflow-hidden">
        <BizShowTags :tags="currentBook.tagList" :center="false" class="mt-8px" />
      </div>
      <div class="text-n-text-2 text-12px line-clamp-3 mt-8px">
        {{ currentBook.introduce }}
      </div>
    </div>
  </ModuleCard>
</template>

<style scoped>
.banner.swiper {
  overflow: hidden;
  padding: 0;
}

.swiper-slide {
  width: 75px;
  height: 150px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.swiper-slide-active {
  width: 96px;
}

.book-cover {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.swiper-slide:not(.swiper-slide-active) .book-cover {
  height: 100px;
}

.swiper-slide-active .book-cover {
  height: 128px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}
</style>
